import { Controller } from "@hotwired/stimulus"
import DescriptionEditor from "../mlt/description_editor"

const POLL_INTERVAL = 3000
const BUILD_DEADLINE = 10 * 60 * 1000

class BuildRequestError extends Error {}

// Drives the transient MLT build screen. Nothing is persisted: the rows live in
// this controller for the life of the page, and the export is assembled in the
// browser rather than written on the server.
export default class extends Controller {
  static targets = [
    "form", "submit", "progress", "progressStage", "errors", "errorMessage",
    "notice", "noticeMessage",
    "results", "list", "count", "preview", "download", "rowTemplate", "previewStyles",
    "totals", "totalsText", "lotGroups", "edit", "done", "resetDescription",
    "reload", "editingStatus"
  ]

  static values = { buildUrl: String, statusUrl: String }

  connect() {
    this.rows = []
    this.selectedIndex = null
    this.request = null
    this.isEditing = false
    this.descriptionEditor = new DescriptionEditor({
      onChange: (description) => this.updateSelectedDescription(description)
    })
  }

  disconnect() {
    this.request?.abort()
    this.descriptionEditor.stop()
    this.restorePage()
  }

  async build(event) {
    event.preventDefault()
    this.request?.abort()
    this.clearMessages()
    this.submitTarget.disabled = true
    this.showProgress("Uploading...")
    const request = new AbortController()
    this.request = request

    try {
      const response = await fetch(this.buildUrlValue, {
        method: "POST",
        body: new FormData(this.formTarget),
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin",
        signal: request.signal
      })
      const payload = await response.json()

      if (response.status === 422) {
        this.hideProgress()
        this.present(payload)
        return
      }
      if (response.status !== 202 || !payload.build_id) throw new BuildRequestError()

      this.updateProgress("Queued")
      const result = await this.poll(payload.build_id, request.signal)
      if (this.request !== request) return

      this.hideProgress()
      this.present(result)
    } catch (error) {
      if (this.request !== request) return

      this.hideProgress()
      if (error.name !== "AbortError") {
        const message = error.message || "The build could not be completed. Please try again."
        this.showErrors([{ message: message }])
      }
    } finally {
      if (this.request === request) {
        this.request = null
        this.submitTarget.disabled = false
      }
    }
  }

  async poll(id, signal) {
    const deadline = Date.now() + BUILD_DEADLINE

    while (Date.now() < deadline) {
      const query = new URLSearchParams({ id: id })
      const response = await fetch(`${this.statusUrlValue}?${query}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: signal
      })
      const payload = await response.json()

      if (response.status === 200) return payload
      if (response.status === 202) this.updateProgress(payload.stage || "Processing...")
      else if (response.status === 404 || response.status === 500) {
        throw new BuildRequestError(payload.message)
      } else {
        throw new BuildRequestError()
      }

      await this.delay(POLL_INTERVAL)
    }

    throw new BuildRequestError("The build is taking longer than expected. Please try again.")
  }

  present(payload) {
    if (payload.unrecognised_headings?.length) this.showNotice(payload.unrecognised_headings)
    if (!payload.ok) return this.showErrors(payload.failures)

    this.rows = payload.rows.map((row) => ({
      ...row,
      originalDescription: row.description
    }))
    this.renderList()
    this.showTotals(payload.totals)
    this.resultsTarget.classList.remove("tw-hidden")
    this.selectIndex(0)
  }

  renderList() {
    this.countTarget.textContent = `${this.rows.length} ${this.rows.length === 1 ? "Lot" : "Lots"}`
    this.listTarget.replaceChildren()

    this.rows.forEach((row, index) => {
      const item = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      item.dataset.index = index
      item.querySelector("[data-row='number']").textContent = row.lot_number
      item.querySelector("[data-row='name']").textContent = row.lot_name
      item.querySelector("[data-row='groups']").textContent = this.describeGroups(row.groups)
      this.showEditedIndicator(item, row)
      this.listTarget.appendChild(item)
    })
  }

  // "4 details · 14 traits · 2 indexes" — groups with nothing in them are left
  // out, so a Lot that resolved almost nothing is obvious at a glance.
  describeGroups(groups) {
    const found = Object.entries(groups || {})
      .filter(([, count]) => count > 0)
      .map(([group, count]) => `${count} ${group}`)

    return found.length ? found.join(" · ") : "no fields resolved"
  }

  showTotals(totals) {
    if (!totals) return

    const parts = Object.entries(totals).map(([group, count]) => `${count} ${group}`)
    this.totalsTextTarget.textContent = `${parts.join(" · ")} across ${this.rows.length} Lots`
    this.totalsTarget.classList.remove("tw-hidden")
  }

  select(event) {
    this.selectIndex(Number(event.currentTarget.dataset.index))
  }

  selectIndex(index) {
    const row = this.rows[index]
    if (!row) return

    this.stopEditing()
    this.selectedIndex = index
    this.listTarget.querySelectorAll("[data-index]").forEach((item) => {
      item.setAttribute("aria-selected", item.dataset.index === String(index))
    })

    this.lotGroupsTarget.textContent = this.describeGroups(row.groups)
    this.replacePreviewFrame(row.description)
    this.updateEditingControls()
  }

  editDescription() {
    if (!this.selectedRow) return

    this.isEditing = this.descriptionEditor.start(this.previewTarget)
    this.updateEditingControls()
  }

  doneEditing() {
    this.stopEditing()
    this.replacePreviewFrame(this.selectedRow.description)
    this.updateEditingControls()
  }

  restoreDescription() {
    const row = this.selectedRow
    if (!row) return

    this.stopEditing()
    row.description = row.originalDescription
    this.updateEditedState()
    this.replacePreviewFrame(row.description)
    this.updateEditingControls()
  }

  retryPreview() {
    const selected = this.listTarget.querySelector("[aria-selected='true']")
    const index = Number(selected?.dataset.index ?? this.selectedIndex ?? 0)
    if (!Number.isInteger(index)) return

    this.selectIndex(index)
  }

  updateSelectedDescription(description) {
    const row = this.selectedRow
    if (!row) return

    row.description = description
    this.updateEditedState()
    this.updateEditingControls()
  }

  stopEditing() {
    if (!this.isEditing) return

    this.descriptionEditor.stop()
    this.isEditing = false
  }

  updateEditedState() {
    const item = this.listTarget.querySelector(`[data-index='${this.selectedIndex}']`)
    if (item) this.showEditedIndicator(item, this.selectedRow)
  }

  showEditedIndicator(item, row) {
    item.querySelector("[data-row='edited']")
      .classList.toggle("tw-hidden", row.description === row.originalDescription)
  }

  updateEditingControls() {
    const edited = this.selectedRow?.description !== this.selectedRow?.originalDescription
    this.editTarget.classList.toggle("tw-hidden", this.isEditing)
    this.doneTarget.classList.toggle("tw-hidden", !this.isEditing)
    this.reloadTarget.classList.toggle("tw-hidden", this.isEditing)
    this.editingStatusTarget.classList.toggle("tw-hidden", !this.isEditing)
    this.resetDescriptionTarget.classList.toggle("tw-hidden", !edited)
  }

  // Reusing one sandboxed iframe occasionally leaves a completed srcdoc
  // navigation displaying a blank document. A fresh frame gives every Lot a
  // fresh browsing context, and retrying does not require another server build.
  replacePreviewFrame(description) {
    const current = this.previewTarget
    const replacement = current.cloneNode(false)
    this.editTarget.disabled = true
    replacement.removeAttribute("srcdoc")
    delete replacement.dataset.previewLoaded
    replacement.addEventListener("load", () => {
      replacement.dataset.previewLoaded = "true"
      this.editTarget.disabled = false
    }, { once: true })
    replacement.srcdoc = `${this.previewStylesTarget.innerHTML}${description}`
    current.replaceWith(replacement)
  }

  download() {
    if (!this.rows.length) return

    // A byte order mark keeps Excel reading the file as UTF-8.
    const csv = "﻿" + [
      ["Lot #", "Lot Name", "Description", "Quantity"],
      ...this.rows.map((row) => [row.lot_number, row.lot_name, row.description, row.quantity])
    ].map((cells) => cells.map((cell) => this.escapeCell(cell)).join(",")).join("\r\n")

    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = "mlt-export.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  escapeCell(cell) {
    return `"${String(cell ?? "").replaceAll('"', '""')}"`
  }

  showErrors(failures) {
    this.errorMessageTarget.textContent = failures.map((failure) => failure.message).join(" ")
    this.errorsTarget.classList.remove("tw-hidden")
  }

  showNotice(headings) {
    this.noticeMessageTarget.textContent =
      `${headings.length} excluded: ${headings.join(", ")}`
    this.noticeTarget.classList.remove("tw-hidden")
  }

  clearMessages() {
    this.errorsTarget.classList.add("tw-hidden")
    this.noticeTarget.classList.add("tw-hidden")
  }

  showProgress(stage) {
    this.previousFocus = document.activeElement
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    this.updateProgress(stage)
    this.progressTarget.classList.remove("tw-hidden")
    this.progressTarget.focus()
  }

  updateProgress(stage) {
    this.progressStageTarget.textContent = stage
  }

  hideProgress() {
    this.progressTarget.classList.add("tw-hidden")
    this.restorePage()
    this.previousFocus?.focus()
    this.previousFocus = null
  }

  restorePage() {
    if (this.previousOverflow !== undefined) document.body.style.overflow = this.previousOverflow
    this.previousOverflow = undefined
  }

  delay(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds))
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  get selectedRow() {
    return this.rows[this.selectedIndex]
  }
}
